import React from "react";
import { AlertCircle, CheckCircle2, Clock3, FileWarning, Info, MapPin, ShieldCheck } from "lucide-react";
export type StatusTone = "green" | "blue" | "amber" | "red" | "slate";
export function StatusChip({
  label,
  tone = "slate"
}: {
  label: string;
  tone?: StatusTone;
}) {
  const styles: Record<StatusTone, string> = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700"
  };
  const Icon = tone === "green" ? CheckCircle2 : tone === "blue" ? Info : tone === "amber" ? Clock3 : tone === "red" ? AlertCircle : FileWarning;
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${styles[tone]}`} data-id="element-92" data-mp="23v0zxvu4ksry"><Icon className="h-3.5 w-3.5" data-id="element-93" data-mp="21kye3wi1ti7" />{label}</span>;
}
export function SectionTitle({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-94" data-mp="3r3f5683hq2ul">
    <div data-id="element-95" data-mp="3d3njrman9k6k">
      {eyebrow && <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[#006a4e]" data-id="element-96" data-mp="3d4fgj0fz9nyf">{eyebrow}</p>}
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-id="element-97" data-mp="3i3ianpuf69rm">{title}</h1>
      {description && <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600" data-id="element-98" data-mp="9nyiyv9eczeo">{description}</p>}
    </div>
    {action}
  </div>;
}
export function KpiCard({
  label,
  value,
  detail,
  tone = "green"
}: {
  label: string;
  value: string;
  detail: string;
  tone?: StatusTone;
}) {
  const colors: Record<StatusTone, string> = {
    green: "border-t-[#006a4e]",
    blue: "border-t-blue-600",
    amber: "border-t-amber-500",
    red: "border-t-red-600",
    slate: "border-t-slate-500"
  };
  return <article className={`rounded-xl border border-slate-200 border-t-4 bg-white p-4 shadow-sm ${colors[tone]}`} data-id="element-99" data-mp="xc9inv3cwt1d">
    <p className="text-sm font-medium text-slate-600" data-id="element-100" data-mp="178rr7qfmj9mc">{label}</p>
    <p className="mt-2 text-2xl font-bold text-slate-900" data-id="element-101" data-mp="1765i3md34m20">{value}</p>
    <p className="mt-1 text-xs text-slate-500" data-id="element-102" data-mp="12aggldhi8v32">{detail}</p>
  </article>;
}
export function InfoPanel({
  children,
  tone = "blue"
}: {
  children: React.ReactNode;
  tone?: "blue" | "amber" | "green";
}) {
  const styles = tone === "blue" ? "border-blue-200 bg-blue-50 text-blue-900" : tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return <div className={`flex gap-3 rounded-xl border p-4 text-sm leading-6 ${styles}`} data-id="element-103" data-mp="jh2u7wruxwz2"><Info className="mt-0.5 h-5 w-5 shrink-0" data-id="element-104" data-mp="ekm6v4shfmn1" /> <div data-id="element-105" data-mp="ehgi5qnrmmru">{children}</div></div>;
}
export function MapPlaceholder({
  compact = false
}: {
  compact?: boolean;
}) {
  return <div className={`relative overflow-hidden rounded-xl border border-dashed border-[#006a4e]/40 bg-[#eaf5ef] ${compact ? "h-40" : "h-64"}`} aria-label="মানচিত্রের নমুনা অবস্থান" data-id="element-106" data-mp="4lddoe5fyc1q">
    <div className="absolute left-[12%] top-[15%] h-20 w-32 rotate-12 rounded-[35%] border-2 border-[#006a4e]/30" data-id="element-107" data-mp="lw6ngad96ug" />
    <div className="absolute bottom-[10%] right-[14%] h-24 w-40 -rotate-6 rounded-[45%] border-2 border-[#006a4e]/30" data-id="element-108" data-mp="2g8qdqp4vu5nm" />
    <div className="absolute left-[43%] top-[40%] h-4 w-4 rounded-full bg-[#f42a41] ring-4 ring-[#f42a41]/20" data-id="element-109" data-mp="2l7bgav7lx1f8" />
    <div className="absolute inset-x-0 bottom-0 border-t border-[#006a4e]/15 bg-white/80 px-3 py-2 text-xs font-medium text-[#006a4e]" data-id="element-110" data-mp="2q766sxngzozr"><MapPin className="mr-1 inline h-3.5 w-3.5" data-id="element-111" data-mp="1skql1jo0g56e" /> মানচিত্র সংযোগের ডেমো প্লেসহোল্ডার — প্রকৃত GPS/সীমানা তথ্য সংযুক্ত নয়</div>
  </div>;
}
export function PrivacyNote() {
  return <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-112" data-mp="22gagy62kr3v5"><ShieldCheck className="h-4 w-4 text-[#006a4e]" data-id="element-113" data-mp="278219yv50dcu" /> সংবেদনশীল তথ্য ভূমিকা অনুযায়ী আড়াল করা হয়েছে</div>;
}